import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Business as BusinessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachMoney as MoneyIcon,
  Payment as PaymentIcon,
  Category as CategoryIcon,
  MoreVert as MoreIcon
} from '@mui/icons-material';
import { 
  Box, 
  Typography, 
  Chip, 
  Tooltip, 
  IconButton, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText 
} from '@mui/material';

// Import components
import EntityListContainer from '../../../components/common/EntityListContainer';
import { EntityCard } from '../../../design-system/components/data';
import EntityStatusBadge from '../../../components/core/EntityStatusBadge';
import VendorDialog from '../../../components/vendors/VendorDialog';

// Import stores
import useVendorStore from '../../../stores/vendorStore';
import useAuthStore from '../../../stores/authStore';
import useUIStore from '../../../stores/uiStore';

// Import utilities
import { formatCurrencyWithSymbol } from '../../../utils/currencyUtils';
import useConfirmationDialog from '../../../hooks/useConfirmationDialog';

// Configuration
const FILTER_CONFIG = [
  {
    name: 'type',
    label: 'Type',
    allLabel: 'All Types',
    options: [
      { value: 'supplier', label: 'Supplier' },
      { value: 'contractor', label: 'Contractor' },
      { value: 'service_provider', label: 'Service Provider' },
      { value: 'freelancer', label: 'Freelancer' },
      { value: 'influencer', label: 'Influencer' },
      { value: 'agency', label: 'Agency' },
      { value: 'consultant', label: 'Consultant' },
      { value: 'other', label: 'Other' }
    ]
  },
  {
    name: 'status',
    label: 'Status',
    allLabel: 'All Statuses',
    options: [
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' },
      { value: 'suspended', label: 'Suspended' }
    ]
  },
  {
    name: 'categories',
    label: 'Category',
    allLabel: 'All Categories',
    options: [
      // Influencer categories
      { value: 'fashion', label: 'Fashion' },
      { value: 'beauty', label: 'Beauty' },
      { value: 'lifestyle', label: 'Lifestyle' },
      { value: 'travel', label: 'Travel' },
      { value: 'food', label: 'Food' },
      { value: 'fitness', label: 'Fitness' },
      // Service categories
      { value: 'marketing', label: 'Marketing' },
      { value: 'design', label: 'Design' },
      { value: 'development', label: 'Development' },
      { value: 'consulting', label: 'Consulting' },
      { value: 'accounting', label: 'Accounting' },
      { value: 'legal', label: 'Legal' },
      { value: 'other', label: 'Other' }
    ]
  }
];

const SORT_OPTIONS = [
  { field: 'name', label: 'Name' },
  { field: 'createdAt', label: 'Date Created' },
  { field: 'type', label: 'Type' },
  { field: 'totalAmount', label: 'Total Amount' },
  { field: 'totalExpenses', label: 'Total Expenses' }
];

const VendorList = () => {
  const navigate = useNavigate();
  
  // Store hooks
  const { fetchVendors, vendors = [], meta, isLoading, error, deleteVendor } = useVendorStore();
  const { hasPermission } = useAuthStore();
  const { addNotification } = useUIStore();
  
  // Debug logging
  console.log('[VendorList] vendors:', vendors);
  console.log('[VendorList] meta:', meta);
  console.log('[VendorList] isLoading:', isLoading);
  console.log('[VendorList] error:', error);
  
  // Confirmation dialog
  const [ConfirmDialog, confirm] = useConfirmationDialog();
  
  // State for action menu
  const [actionMenuState, setActionMenuState] = React.useState({});
  
  // Check permissions
  const canCreate = hasPermission('vendors', 'create');
  const canUpdate = hasPermission('vendors', 'update');
  const canDelete = hasPermission('vendors', 'delete');
  
  // Handle vendor deletion with confirmation
  const handleDeleteVendor = useCallback(async (vendor) => {
    const confirmed = await confirm({
      title: `Delete ${vendor.name}?`,
      content: `This will permanently delete the vendor. This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDelete: true
    });
    
    if (confirmed) {
      try {
        await deleteVendor(vendor._id);
        addNotification({
          message: `Vendor "${vendor.name}" deleted successfully`,
          type: 'success'
        });
      } catch (error) {
        addNotification({
          message: `Failed to delete vendor: ${error.message}`,
          type: 'error'
        });
      }
    }
  }, [confirm, deleteVendor, addNotification]);
  
  // Entity renderer for grid view
  const renderVendor = useCallback((vendor) => {
    if (!vendor || !vendor._id) return null;
    
    return (
      <EntityCard
        key={vendor._id}
        title={vendor.name}
        subtitle={vendor.code}
        status={vendor.status}
        entityType="vendor"
        actions={[
          {
            label: 'View',
            icon: <BusinessIcon />,
            onClick: () => navigate(`/finances/vendors/${vendor._id}`)
          },
          ...(canUpdate ? [{
            label: 'Edit',
            icon: <EditIcon />,
            onClick: () => navigate(`/finances/vendors/${vendor._id}/edit`)
          }] : []),
          ...(canDelete ? [{
            label: 'Delete',
            icon: <DeleteIcon />,
            onClick: () => handleDeleteVendor(vendor),
            color: 'error'
          }] : [])
        ]}
        onClick={() => navigate(`/finances/vendors/${vendor._id}`)}
      >
        <Box sx={{ mt: 2 }}>
          {vendor.description && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {vendor.description}
            </Typography>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Chip
              label={vendor.type.replace('_', ' ').toUpperCase()}
              size="small"
              color="primary"
              variant="outlined"
            />
            {vendor.contact?.email && (
              <Typography variant="body2" color="textSecondary">
                {vendor.contact.email}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <MoneyIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" color="textSecondary">
                  Total: {formatCurrencyWithSymbol(vendor.totalAmount || 0)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PaymentIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" color="textSecondary">
                  {vendor.totalExpenses || 0} expenses
                </Typography>
              </Box>
            </Box>
            
            {vendor.categories && vendor.categories.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {vendor.categories.slice(0, 2).map((category, index) => (
                  <Chip
                    key={index}
                    label={category.replace('_', ' ')}
                    size="small"
                    variant="outlined"
                  />
                ))}
                {vendor.categories.length > 2 && (
                  <Chip
                    label={`+${vendor.categories.length - 2}`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      </EntityCard>
    );
  }, [canUpdate, canDelete, navigate, handleDeleteVendor]);
  
  // Define table columns
  const tableColumns = [
    {
      id: 'name',
      label: 'Vendor Name',
      renderCell: (vendor) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BusinessIcon color="primary" />
          <Box>
            <Typography variant="body1" fontWeight={600}>
              {vendor.name}
            </Typography>
            {vendor.code && (
              <Typography variant="caption" color="textSecondary">
                {vendor.code}
              </Typography>
            )}
          </Box>
        </Box>
      )
    },
    {
      id: 'type',
      label: 'Type',
      align: 'center',
      renderCell: (vendor) => (
        <Chip
          label={vendor.type.replace('_', ' ').toUpperCase()}
          size="small"
          color="primary"
          variant="outlined"
        />
      )
    },
    {
      id: 'status',
      label: 'Status',
      align: 'center',
      renderCell: (vendor) => (
        <EntityStatusBadge
          status={vendor.status}
          entityType="vendor"
          size="small"
          variant="outlined"
        />
      )
    },
    {
      id: 'contact',
      label: 'Contact',
      renderCell: (vendor) => (
        <Box>
          {vendor.contact?.email && (
            <Typography variant="body2">{vendor.contact.email}</Typography>
          )}
          {vendor.contact?.phone && (
            <Typography variant="caption" color="textSecondary">
              {vendor.contact.phone}
            </Typography>
          )}
        </Box>
      )
    },
    {
      id: 'categories',
      label: 'Categories',
      renderCell: (vendor) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {vendor.categories?.slice(0, 2).map((category, index) => (
            <Chip
              key={index}
              label={category.replace('_', ' ')}
              size="small"
              variant="outlined"
            />
          ))}
          {vendor.categories?.length > 2 && (
            <Chip
              label={`+${vendor.categories.length - 2}`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      )
    },
    {
      id: 'stats',
      label: 'Financial',
      align: 'center',
      renderCell: (vendor) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="body2" fontWeight={500}>
            {formatCurrencyWithSymbol(vendor.totalAmount || 0)}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {vendor.totalExpenses || 0} expenses
          </Typography>
        </Box>
      )
    }
  ];
  
  return (
    <>
      <EntityListContainer
        title="Vendors"
        description="Manage your vendors and suppliers"
        fetchData={fetchVendors}
        renderEntity={renderVendor}
        entities={vendors}
        isLoading={isLoading}
        error={error}
        metadata={meta}
        initialSort={{ field: 'name', direction: 'asc' }}
        filterConfig={FILTER_CONFIG}
        sortOptions={SORT_OPTIONS}
        emptyState={{
          title: 'No vendors found',
          message: 'Start by adding your first vendor',
          buttonLabel: 'Add Vendor',
          icon: <BusinessIcon />,
          onAction: canCreate ? () => navigate('/finances/vendors/create') : undefined
        }}
        actions={{
          canCreate,
          createLabel: 'Add Vendor',
          onCreate: () => navigate('/finances/vendors/create')
        }}
        availableViews={['grid', 'table']}
        defaultView="grid"
        tableConfig={{
          columns: tableColumns,
          onRowClick: (vendor) => navigate(`/finances/vendors/${vendor._id}`),
          renderRowActions: (vendor) => {
            const vendorId = vendor._id;
            const anchorEl = actionMenuState[vendorId];
            const actions = [
              {
                label: 'Edit',
                icon: <EditIcon />,
                onClick: () => navigate(`/finances/vendors/${vendor._id}/edit`),
                disabled: !canUpdate
              },
              {
                label: 'Delete',
                icon: <DeleteIcon />,
                onClick: () => handleDeleteVendor(vendor),
                disabled: !canDelete,
                color: 'error'
              }
            ].filter(action => !action.disabled);

            return (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setActionMenuState(prev => ({
                      ...prev,
                      [vendorId]: e.currentTarget
                    }));
                  }}
                >
                  <MoreIcon fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setActionMenuState(prev => ({
                    ...prev,
                    [vendorId]: null
                  }))}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  {actions.map((action, index) => (
                    <MenuItem 
                      key={index} 
                      onClick={(e) => {
                        e.stopPropagation();
                        action.onClick();
                        setActionMenuState(prev => ({
                          ...prev,
                          [vendorId]: null
                        }));
                      }}
                      sx={{
                        color: action.color === 'error' ? 'error.main' : undefined
                      }}
                    >
                      <ListItemIcon sx={{ 
                        color: action.color === 'error' ? 'error.main' : undefined 
                      }}>
                        {action.icon}
                      </ListItemIcon>
                      <ListItemText primary={action.label} />
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            );
          }
        }}
      />
      
      <ConfirmDialog />
    </>
  );
};

export default VendorList;